import type { ITeamMembers } from "@ports/teamMembers/ITeamMembers";
import type { CreateOperatorInput, TeamMember } from "@domain/CRM/teamMember";
import { generateRandomPassword } from "@domain/Auth";

/** Create-operator form data — everything except the password (generated here). */
export type CreateOperatorFormData = Omit<CreateOperatorInput, "password">;

export interface PasswordGeneratorUseCasesDeps {
	teamMembersApi: ITeamMembers;
	/** Pluggable random-password generator. Defaults to a CSPRNG-backed
	 *  alphanumeric generator using `crypto.getRandomValues`. Tests inject
	 *  a deterministic stub. */
	generatePassword?: () => string;
}

/**
 * Drives the Admin → Password Generator page's workflows:
 *  - `generateAndSave(teamMemberId)` sets a fresh password on an existing operator.
 *  - `createOperatorAndGeneratePassword(input)` onboards a brand-new operator and
 *    generates its login password in one step.
 *  - `listActivePasswordlessMembers()` lists existing operators without a password.
 *
 * The password is returned so the page can surface it to the operator. Errors
 * propagate; the vm catches and toasts.
 */
export interface PasswordGeneratorUseCases {
	generateAndSave(teamMemberId: string): Promise<string>;
	createOperatorAndGeneratePassword(
		input: CreateOperatorFormData,
	): Promise<{ teamMember: TeamMember; password: string }>;
	listActivePasswordlessMembers(): Promise<TeamMember[]>;
}

async function generateAndSave(deps: PasswordGeneratorUseCasesDeps, teamMemberId: string): Promise<string> {
	const password = (deps.generatePassword ?? generateRandomPassword)();
	await deps.teamMembersApi.setPassword(teamMemberId, password);
	return password;
}

async function createOperatorAndGeneratePassword(
	deps: PasswordGeneratorUseCasesDeps,
	input: CreateOperatorFormData,
): Promise<{ teamMember: TeamMember; password: string }> {
	const password = (deps.generatePassword ?? generateRandomPassword)();
	const teamMember = await deps.teamMembersApi.createOperator({ ...input, password });
	return { teamMember, password };
}

export function makePasswordGeneratorUseCases(deps: PasswordGeneratorUseCasesDeps): PasswordGeneratorUseCases {
	return {
		generateAndSave: (teamMemberId) => generateAndSave(deps, teamMemberId),
		createOperatorAndGeneratePassword: (input) => createOperatorAndGeneratePassword(deps, input),
		listActivePasswordlessMembers: () => deps.teamMembersApi.listActivePasswordless(),
	};
}
